import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import logo from '../assets/images/ABN1.png';
import { GET } from '../services/api';
import { MasterChapterSelect } from '../context/MasterChapter';
import {
  LayoutDashboard, CalendarDays, UsersRound, ClipboardList,
  Settings2, LogOut, Lock, ChevronDown,
  Menu, Briefcase, UserCheck, Award, IdCard,
  X, AlertTriangle, Handshake, Ticket, Megaphone, Globe, Building2, CircleHelp, CalendarRange, BellRing
} from 'lucide-react';

const scrollbarStyles = `
  .sidebar-scrollbar::-webkit-scrollbar {
    width: 4px;
  }
  .sidebar-scrollbar::-webkit-scrollbar-track {
    background: transparent;
    border-radius: 10px;
  }
  .sidebar-scrollbar::-webkit-scrollbar-thumb {
    background-color: #cbd5e1;
    border-radius: 10px;
    border: 1px solid #e2e8f0;
  }
  .sidebar-scrollbar::-webkit-scrollbar-thumb:hover {
    background-color: #1a87cf;
  }
`;

const StyleSheet = () => <style>{scrollbarStyles}</style>;

const Sidebar = ({ handleLogout }) => {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [unreadHelpCount, setUnreadHelpCount] = useState(0);

  // State for Logout Confirmation Popup
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  // User Role Logic
  const userStr = localStorage.getItem('user');
  const user = userStr ? JSON.parse(userStr) : {};
  const isSuperAdmin = user.role === 'super-admin';
  const isAdminUser = ['super-admin', 'admin', 'sub-admin'].includes(user.role);
  const roleLabel = { 'super-admin': 'Super admin', 'admin': 'Admin', 'sub-admin': 'Sub admin' }[user.role] || 'Admin';

  // Close dropdowns on route change
  useEffect(() => {
    setSidebarOpen(false);
    setShowProfileDropdown(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isSuperAdmin) {
      setUnreadHelpCount(0);
      return;
    }

    let mounted = true;

    const fetchUnreadCount = async () => {
      try {
        const data = await GET('/help-center/unread-count');
        const count = Number(
          data?.unreadCount ?? data?.count ?? data?.data?.unreadCount ?? data?.data?.count ?? 0
        );
        if (mounted) setUnreadHelpCount(Number.isNaN(count) ? 0 : count);
      } catch (error) {
        if (mounted) setUnreadHelpCount(0);
        console.error('Failed to fetch Help Center unread count:', error);
      }
    };

    fetchUnreadCount();
    const intervalId = setInterval(fetchUnreadCount, 30000);
    return () => {
      mounted = false;
      clearInterval(intervalId);
    };
  }, [location.pathname, isSuperAdmin]);

  // --- Menu Configuration (Updated Icons) ---
  const menuItems = [
    { path: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/admin/events', label: 'Events', icon: CalendarDays },
    { path: '/admin/meeting', label: 'Meetings', icon: Handshake },
    { path: '/admin/users', label: 'Users', icon: UsersRound },
    { path: '/admin/business-report', label: 'Chapter Report', icon: Building2 },
    { path: '/admin/attendance-report', label: 'Attendance Report', icon: ClipboardList },
    { path: '/admin/committee', label: 'Committee', icon: Award },
    ...(isAdminUser ? [{ path: '/admin/tenure', label: 'Tenure', icon: CalendarRange }] : []),
    ...(isAdminUser ? [{ path: '/admin/membership', label: 'Membership', icon: IdCard }] : []),

    ...(isSuperAdmin ? [{ path: '/admin/banner-sponsor', label: 'Sponcer', icon: Briefcase }] : []),
    ...(isSuperAdmin ? [{ path: '/admin/help-center', label: 'Help Center', icon: CircleHelp, badgeCount: unreadHelpCount }] : []),
    ...(isAdminUser ? [{ path: '/admin/active-codes', label: 'Active Codes', icon: Ticket }] : []),
    ...(isAdminUser ? [{ path: '/admin/visitors', label: 'Visitors', icon: UserCheck }] : []),
    ...(isSuperAdmin ? [{ path: '/admin/communication', label: 'Communication', icon: Megaphone }] : []),
    ...(isSuperAdmin ? [{ path: '/admin/push-notifications', label: 'Push Notifications', icon: BellRing }] : []),
    ...(isSuperAdmin ? [{ path: '/admin/landing-page', label: 'Landing Page', icon: Globe }] : []),
    ...(isSuperAdmin ? [{ path: '/admin/setting', label: 'Settings', icon: Settings2 }] : []),
  ];

  // Sub-routes belong to their parent entry: /admin/users/:id keeps Users lit
  // and titled, rather than falling back to Dashboard.
  const isActive = (path) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);

  const getPageTitle = () => {
    const currentItem = menuItems.find(item => item.path && isActive(item.path));
    return currentItem ? currentItem.label : 'Dashboard';
  };

  // --- Logout Logic ---
  const handleLogoutClick = () => {
    setShowProfileDropdown(false); // Close dropdown
    setShowLogoutConfirm(true);    // Open Popup
  };

  const confirmLogout = () => {
    setShowLogoutConfirm(false);
    if (handleLogout) handleLogout();
  };

  return (
    <>
      <StyleSheet />
      {/* Mobile Backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm lg:hidden z-40 transition-opacity"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* ======================= SIDEBAR ======================= */}
      <div
        className={`fixed top-0 left-0 h-full w-64 bg-white border-r border-slate-200 shadow-2xl lg:shadow-none transform transition-transform duration-300 ease-in-out z-50 
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}
      >
        {/* Logo Area */}
        <div className="h-16 flex items-center justify-between border-b border-slate-200 px-5">
          <img src={logo} alt="Ahir Business Network" className="h-8 w-auto object-contain" />

          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-2 text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Navigation */}
        <nav className="p-3 space-y-0.5 overflow-y-auto h-[calc(100%-64px)] pb-8 sidebar-scrollbar">
          {menuItems.map((item, index) => {
            const Icon = item.icon;

            const active = isActive(item.path);
            return (
              <Link
                key={index}
                to={item.path}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors group relative
                ${active
                    ? 'bg-blue-600 text-white shadow-sm shadow-blue-600/30'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
              >
                <Icon className={`w-5 h-5 transition-colors ${active ? 'text-white' : 'text-slate-400 group-hover:text-slate-600'}`} />
                <span className="relative z-10">{item.label}</span>
                {item.badgeCount > 0 && (
                  <span className={`ml-auto min-w-5 h-5 px-1.5 rounded-full text-[11px] leading-5 text-center font-bold ${active ? 'bg-white text-blue-700' : 'bg-red-500 text-white'}`}>
                    {item.badgeCount > 99 ? '99+' : item.badgeCount}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>
      </div>

      {/* ======================= TOP HEADER ======================= */}
      <header className="fixed top-0 right-0 left-0 lg:left-64 bg-white/90 backdrop-blur-md border-b border-slate-200 z-30 h-16">
        <div className="flex items-center justify-between px-4 sm:px-8 h-full max-w-full">

          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 rounded-xl text-slate-600 hover:bg-slate-100 transition-colors"
            >
              <Menu className="w-6 h-6" />
            </button>
            <h1 className="text-base font-bold text-slate-900 tracking-tight sm:text-lg">
              {getPageTitle()}
            </h1>
          </div>

          <div className="flex items-center gap-3">
          <MasterChapterSelect />
          <div className="relative">
            <button
              onClick={() => setShowProfileDropdown(!showProfileDropdown)}
              className={`flex items-center gap-3 p-1.5 pr-3 rounded-full border transition-all duration-200 group
              ${showProfileDropdown ? 'bg-slate-50 border-slate-300' : 'border-slate-100 hover:border-slate-300 hover:bg-white'}`}
            >
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-sm font-bold shadow-sm ring-2 ring-white">
                {user.first_name?.[0] || 'A'}
              </div>
              <div className="hidden md:block text-left">
                <p className="text-sm font-bold text-slate-700 group-hover:text-slate-900 leading-none">
                  {user.first_name || 'Admin'}
                </p>
                <p className="text-[11px] text-slate-500 mt-0.5 font-medium">{roleLabel}</p>
              </div>
              <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${showProfileDropdown ? 'rotate-180 text-blue-500' : ''}`} />
            </button>

            {/* Profile Dropdown */}
            {showProfileDropdown && (
              <>
                <div
                  className="fixed inset-0 z-30"
                  onClick={() => setShowProfileDropdown(false)}
                />
                <div className="absolute right-0 mt-3 w-60 bg-white rounded-2xl shadow-xl border border-slate-100 py-2 z-40 animate-in fade-in zoom-in-95 origin-top-right ring-1 ring-black/5">
                  <div className="px-5 py-4 border-b border-slate-50 md:hidden bg-slate-50/50">
                    <p className="text-sm font-bold text-slate-800">{user.first_name || 'Admin'}</p>
                    <p className="text-xs text-slate-500 font-medium">{roleLabel}</p>
                  </div>

                  <div className="py-1">
                    <Link
                      to="/admin/change-password"
                      onClick={() => setShowProfileDropdown(false)}
                      className="flex items-center gap-3 px-5 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50 hover:text-blue-600 transition-colors"
                    >
                      <Lock className="w-4 h-4" /> Change Password
                    </Link>
                  </div>

                  <div className="border-t border-slate-100 my-1"></div>

                  <div className="py-1">
                    <button
                      onClick={handleLogoutClick}
                      className="w-full flex items-center gap-3 px-5 py-2.5 text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <LogOut className="w-4 h-4" /> Sign Out
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
          </div>
        </div>
      </header>

      {/* ======================= LOGOUT CONFIRMATION MODAL ======================= */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex justify-center items-center z-[100] p-4 animate-in fade-in">
          <div className="bg-white p-6 rounded-3xl shadow-2xl border border-slate-100 w-full max-w-sm text-center scale-100 animate-in zoom-in-95">
            <div className="w-14 h-14 bg-red-50 text-red-600 rounded-full flex items-center justify-center mx-auto mb-4 border-4 border-red-50">
              <AlertTriangle className="w-6 h-6" />
            </div>
            <h3 className="text-lg font-bold text-slate-800 mb-2">Sign Out</h3>
            <p className="text-slate-500 mb-6 font-medium">Are you sure you want to sign out of your account?</p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowLogoutConfirm(false)}
                className="flex-1 px-4 py-2.5 border border-slate-300 rounded-xl hover:bg-slate-50 text-slate-700 font-bold transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmLogout}
                className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl font-bold transition-colors shadow-lg shadow-red-200"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Sidebar;
